/**
 * Landmarks + helpers matemáticos para análisis biomecánico.
 * 33 puntos de MediaPipe Pose.
 * Todo el cómputo ocurre en el cliente. Nada se persiste.
 */

export const LM = {
  NOSE: 0,
  L_EYE_INNER: 1, L_EYE: 2, L_EYE_OUTER: 3,
  R_EYE_INNER: 4, R_EYE: 5, R_EYE_OUTER: 6,
  L_EAR: 7, R_EAR: 8,
  MOUTH_L: 9, MOUTH_R: 10,
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_ELBOW: 13,    R_ELBOW: 14,
  L_WRIST: 15,    R_WRIST: 16,
  L_PINKY: 17,    R_PINKY: 18,
  L_INDEX: 19,    R_INDEX: 20,
  L_THUMB: 21,    R_THUMB: 22,
  L_HIP: 23,      R_HIP: 24,
  L_KNEE: 25,     R_KNEE: 26,
  L_ANKLE: 27,    R_ANKLE: 28,
  L_HEEL: 29,     R_HEEL: 30,
  L_FOOT_IDX: 31, R_FOOT_IDX: 32,
};

/** Aliases legibles -> índice de landmark */
export const ALIAS = {
  nose: LM.NOSE,
  shoulder_L: LM.L_SHOULDER, shoulder_R: LM.R_SHOULDER,
  elbow_L: LM.L_ELBOW, elbow_R: LM.R_ELBOW,
  wrist_L: LM.L_WRIST, wrist_R: LM.R_WRIST,
  hip_L: LM.L_HIP, hip_R: LM.R_HIP,
  knee_L: LM.L_KNEE, knee_R: LM.R_KNEE,
  ankle_L: LM.L_ANKLE, ankle_R: LM.R_ANKLE,
  heel_L: LM.L_HEEL, heel_R: LM.R_HEEL,
  foot_idx_L: LM.L_FOOT_IDX, foot_idx_R: LM.R_FOOT_IDX,
};

/**
 * Obtiene un punto (x,y,z) a partir de un alias o pseudo-punto.
 * Soporta:
 *   - alias plano "shoulder_L"
 *   - genéricos con lado: "shoulder" (promedia L y R)
 *   - compuestos: "hip_mid", "shoulder_mid", "ankle_mid"
 */
export function resolvePoint(lm, name, side /* 'L' | 'R' | undefined */) {
  if (!lm) return null;
  if (name.endsWith('_mid')) {
    const base = name.replace('_mid', '');
    const lKey = ALIAS[`${base}_L`], rKey = ALIAS[`${base}_R`];
    if (lKey == null || rKey == null) return null;
    return midpoint(lm[lKey], lm[rKey]);
  }
  if (side && ALIAS[`${name}_${side}`] != null) return lm[ALIAS[`${name}_${side}`]];
  if (ALIAS[name] != null) return lm[ALIAS[name]];
  // Si viene como "shoulder" sin lado y tenemos ambos, promediar
  const lKey = ALIAS[`${name}_L`], rKey = ALIAS[`${name}_R`];
  if (lKey != null && rKey != null) return midpoint(lm[lKey], lm[rKey]);
  return null;
}

export function midpoint(a, b) {
  if (!a || !b) return null;
  return {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
    z: ((a.z || 0) + (b.z || 0)) / 2,
    visibility: Math.min(a.visibility ?? 1, b.visibility ?? 1),
  };
}

/** Ángulo en el vértice B del triángulo A-B-C, en grados (0..180) */
export function angle(a, b, c) {
  if (!a || !b || !c) return NaN;
  const v1 = { x: a.x - b.x, y: a.y - b.y };
  const v2 = { x: c.x - b.x, y: c.y - b.y };
  const dot = v1.x * v2.x + v1.y * v2.y;
  const m1 = Math.hypot(v1.x, v1.y);
  const m2 = Math.hypot(v2.x, v2.y);
  if (m1 < 1e-6 || m2 < 1e-6) return NaN;
  const cos = Math.min(1, Math.max(-1, dot / (m1 * m2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

/** Distancia euclidiana 2D */
export function distance(a, b) {
  if (!a || !b) return NaN;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Altura del bounding box (alto del cuerpo normalizado, ~1.0) */
export function bodyHeight(lm) {
  const ys = lm.filter(p => (p.visibility ?? 1) > 0.3).map(p => p.y);
  if (ys.length < 4) return 1;
  return Math.max(...ys) - Math.min(...ys);
}

/**
 * Linealidad: distancia máxima perpendicular entre puntos intermedios y la recta
 * que forman los extremos. Normalizada por la altura del cuerpo.
 * Devuelve un número ~ 0 (alineado) a ~ 0.2 (muy torcido).
 */
export function linealidad(points, bh) {
  if (points.length < 3) return 0;
  const A = points[0];
  const C = points[points.length - 1];
  const abx = C.x - A.x, aby = C.y - A.y;
  const len = Math.hypot(abx, aby);
  if (len < 1e-6) return 0;
  let maxDev = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const B = points[i];
    const dev = Math.abs((abx * (A.y - B.y) - (A.x - B.x) * aby) / len);
    if (dev > maxDev) maxDev = dev;
  }
  return maxDev / Math.max(bh, 1e-6);
}

/** Promedio de varios ángulos ignorando NaN */
export function avgAngle(values) {
  const ok = values.filter(v => Number.isFinite(v));
  if (!ok.length) return NaN;
  return ok.reduce((a, b) => a + b, 0) / ok.length;
}

/** Centro de masa aproximado (cadera media) */
export function centerOfMass(lm) {
  return midpoint(lm[LM.L_HIP], lm[LM.R_HIP]);
}
